import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';

import { CharlieTable } from '@atlassian/charlie-table/compiled';
import type { ColumnDef } from '@atlassian/charlie-table/tanstack-helpers';

import type { ContentMetricRow } from './ContentMetricsTable.tsx';

const i18n = defineMessages({
	title: {
		id: 'performance-insights.content-metrics.empty-state.title.non-final',
		defaultMessage: "Nothing matches what you're looking for",
		description:
			'Heading displayed in the content metrics table when no results match the applied filters or search query. Shown to Confluence admins on the Performance analytics page.',
	},
	subtext: {
		id: 'performance-insights.content-metrics.empty-state.subtext.non-final',
		defaultMessage: 'Remove any filters, or search for something less specific',
		description:
			'Subtext displayed below the empty state heading in the content metrics table when no results match. Suggests the admin adjust their filters or search query.',
	},
});

const styles = cssMap({
	headerCell: {
		font: token('font.heading.xxsmall'),
	},
	emptyMessageContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		textAlign: 'center',
	},
});

export interface ContentMetricsEmptyStateProps {
	columnDefs: ColumnDef<ContentMetricRow>[];
}

export const ContentMetricsEmptyState = ({ columnDefs }: ContentMetricsEmptyStateProps) => {
	return (
		<Box>
			<CharlieTable<ContentMetricRow>
				columnDefs={columnDefs}
				data={[]}
				headerCellXcss={styles.headerCell}
			/>
			<Stack xcss={styles.emptyMessageContainer} space="space.100">
				<Heading size="small">
					<FormattedMessage {...i18n.title} />
				</Heading>
				<Text color="color.text.subtlest">
					<FormattedMessage {...i18n.subtext} />
				</Text>
			</Stack>
		</Box>
	);
};
